/**
 * PermissionSourceModal — „z czego wynika to uprawnienie" (F24 Kawałek 1).
 *
 * Komórka w drzewie mówi TYLKO, że prawo płynie z góry („Zgodnie z rolą") i jaki jest wynik
 * (znacznik wolno / nie wolno). Przy kilku rolach i kilku zespołach to za mało: administrator
 * pyta „która z nich to zrobiła" i „czemu wygrał zakaz", a odpowiedzi nie da się wcisnąć w
 * jedną ramkę ani w podpowiedź pod kursorem.
 *
 * Dlatego okienko rozpisuje KAŻDY podmiot osobno (rola/zespół → stan → na jakim poziomie
 * obiektu to ustawiono), a na dole pokazuje, co z tego wyszło i dlaczego. Każdy wiersz ma
 * przejście do uprawnień tego podmiotu — poprawka i tak robi się tam, nie tutaj.
 *
 * Rozbicie liczy ten sam rdzeń, co serwer (`explainPermission` z `@saas/shared`), więc
 * okienko nie może „wytłumaczyć" czegoś innego, niż serwer faktycznie zrobi.
 *
 * Przy OSOBIE podmioty są pogrupowane w TRZY WARSTWY (ADR-0103): Użytkownik → Zespoły → Role.
 * Każda warstwa ma swój wynik; rozstrzyga najwyższa, która się wypowiada — niższe są pokazane,
 * ale wyszarzone („nie liczona"), bo administrator pyta właśnie „czemu zakaz roli nie działa".
 */

import { useTranslation } from 'react-i18next';
import {
  LAYER_ORDER,
  type PermissionLayer,
  type PermissionObjectKind,
  type PermissionSubjectKind,
} from '@saas/shared';
import { ArrowRight, Check, Minus, X } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { InfoHint } from '@/components/InfoHint';
import { Modal } from '@/components/Modal';
import { cn } from '@/lib/utils';

/** Kolejność warstw = priorytet (pierwsza najważniejsza) — wprost z silnika. */
const LAYERS: readonly PermissionLayer[] = LAYER_ORDER;

/** Jak wypadł JEDEN podmiot (osoba, rola albo zespół) dla rozpatrywanej komórki. */
export interface PermissionSourceEntry {
  kind: PermissionSubjectKind;
  id: string;
  name: string;
  /** `null` = ten podmiot nie ma na tej ścieżce żadnej reguły, czyli nic nie wnosi. */
  effect: 'allow' | 'deny' | null;
  /** Poziom obiektu, na którym wisi decydująca reguła — „na kartotece" vs „na typie". */
  objectKind: PermissionObjectKind | null;
}

export interface PermissionSourceModalProps {
  open: boolean;
  onClose: () => void;
  /** Nazwa obiektu i akcji, o które pytamy — nagłówek musi mówić, której komórki dotyczy. */
  objectLabel: string;
  actionLabel: string;
  /** Rozbicie na podmioty (przy podmiocie OSOBA). Puste, gdy patrzymy na rolę albo zespół. */
  entries: PermissionSourceEntry[];
  /** Wynik końcowy komórki po złożeniu wszystkiego. */
  result: boolean;
  /** Podmiot, który przesądził. `null` = nikt nie miał reguły (odmowa domyślna). */
  decidedBy: { kind: PermissionSubjectKind; name: string } | null;
  /** Czy wynik wziął się z własnego ustawienia osoby (wtedy role i zespoły nie są liczone). */
  ownOverride?: boolean;
  /**
   * Wynik każdej warstwy (przy osobie). Gdy podany, wiersze są grupowane po warstwach, a warstwa
   * `decidedLayer` oznaczona jako rozstrzygająca.
   */
  layerResults?: Partial<Record<PermissionLayer, 'allow' | 'deny' | null>>;
  decidedLayer?: PermissionLayer | null;
  /** Przejście do uprawnień podmiotu — zamyka okienko i przestawia ekran. */
  onGoToSubject?: (kind: PermissionSubjectKind, id: string) => void;
}

export function PermissionSourceModal({
  open,
  onClose,
  objectLabel,
  actionLabel,
  entries,
  result,
  decidedBy,
  ownOverride = false,
  layerResults,
  decidedLayer = null,
  onGoToSubject,
}: PermissionSourceModalProps) {
  const { t } = useTranslation();

  /** Znacznik stanu jednego podmiotu — trzy możliwości, w tym „nic nie ustawiono". */
  function stateCell(entry: PermissionSourceEntry) {
    if (entry.effect == null) {
      return (
        <span className="inline-flex items-center gap-1 text-text-muted">
          <Minus size={14} aria-hidden="true" />
          {t('tenant.permissions.center.why.noRule')}
        </span>
      );
    }
    const allow = entry.effect === 'allow';
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 font-medium',
          allow ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400',
        )}
      >
        {allow ? <Check size={14} aria-hidden="true" /> : <X size={14} aria-hidden="true" />}
        {allow ? t('tenant.permissions.tri.allow') : t('tenant.permissions.tri.deny')}
      </span>
    );
  }

  const deciding = entries.filter((e) => e.effect != null);
  // Kaskada warstw (Użytkownik → Zespoły → Role): o zdaniu decyduje WYNIK warstwy, która
  // rozstrzygnęła — zakaz roli nie ma znaczenia, gdy przesądził zespół z zezwoleniem.
  const hasDeny = decidedBy != null && !result;

  /**
   * Zdanie wyjaśniające — którą z zasad zadziałało. Bez tego okienko pokazuje rozbieżność
   * („rola pozwala, zespół zabrania") i zostawia człowieka z pytaniem, czemu wynik jest taki.
   */
  const reason = ownOverride
    ? t('tenant.permissions.center.why.reasonOwn')
    : hasDeny
      ? t('tenant.permissions.center.why.reasonDeny')
      : deciding.length > 0
        ? t('tenant.permissions.center.why.reasonAllow')
        : t('tenant.permissions.center.why.reasonNone');

  /** Wynik warstwy jako plakietka — ten sam język co reszta aplikacji (Badge wg tokenów). */
  function layerBadge(value: 'allow' | 'deny' | null) {
    if (value == null) {
      return (
        <Badge tone="neutral" size="sm">
          {t('tenant.permissions.center.why.layerSilent')}
        </Badge>
      );
    }
    return value === 'allow' ? (
      <Badge tone="success" size="sm" icon={Check}>
        {t('tenant.permissions.tri.allow')}
      </Badge>
    ) : (
      <Badge tone="danger" size="sm" icon={X}>
        {t('tenant.permissions.tri.deny')}
      </Badge>
    );
  }

  /** Wiersz podmiotu — nazwa, jego ustawienie, poziom obiektu i przejście do uprawnień. */
  function subjectRow(entry: PermissionSourceEntry, withKind: boolean) {
    return (
      <li
        key={`${entry.kind}:${entry.id}`}
        className="grid grid-cols-[minmax(0,1fr)_8.5rem_7rem_minmax(0,auto)] items-center gap-3 px-4 py-2.5 text-sm"
      >
        <span className="min-w-0 truncate">
          {withKind && (
            <span className="text-text-muted">
              {t(`tenant.permissions.center.subject.${entry.kind}`)}{' '}
            </span>
          )}
          <span className="font-medium text-text-primary">{entry.name}</span>
        </span>
        <span>{stateCell(entry)}</span>
        <span className="truncate text-text-secondary">
          {entry.objectKind ? t(`tenant.permissions.center.why.level.${entry.objectKind}`) : '—'}
        </span>
        <span className="text-right">
          {onGoToSubject && entry.kind !== 'user' ? (
            <Button variant="ghost" size="sm" onClick={() => onGoToSubject(entry.kind, entry.id)}>
              {t('tenant.permissions.center.why.goTo')}
              <ArrowRight size={14} aria-hidden="true" />
            </Button>
          ) : null}
        </span>
      </li>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={t('tenant.permissions.center.why.title')}
      description={t('tenant.permissions.center.why.subtitle', {
        object: objectLabel,
        action: actionLabel.toLowerCase(),
      })}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {layerResults ? (
          // Trzy warstwy jako osobne karty (Workflow 3.0: radius 12, border tokenowy). Warstwa
          // rozstrzygająca ma wyróżnioną ramkę; niższe od niej są przygaszone — nie liczą się.
          LAYERS.map((layer, index) => {
            const rows = entries.filter((e) => e.kind === layer);
            const decides = decidedLayer === layer;
            const skipped = decidedLayer != null && index > LAYERS.indexOf(decidedLayer);
            return (
              <Card
                key={layer}
                padding="none"
                data-testid={`why-layer-${layer}`}
                className={cn(
                  'overflow-hidden',
                  decides &&
                    'border-accent-300 ring-1 ring-accent-200 dark:border-accent-700 dark:ring-accent-800',
                  skipped && 'opacity-60',
                )}
              >
                <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                  <span
                    className={cn(
                      'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                      decides ? 'bg-accent-600 text-white' : 'bg-bg-tertiary text-text-secondary',
                    )}
                    aria-hidden="true"
                  >
                    {index + 1}
                  </span>
                  <span className="text-sm font-semibold text-text-primary">
                    {t(`tenant.permissions.center.why.layer.${layer}`)}
                  </span>
                  {layerBadge(layerResults[layer] ?? null)}
                  <span className="ml-auto">
                    {decides ? (
                      <Badge tone="accent" size="sm" data-testid="why-layer-decides">
                        {t('tenant.permissions.center.why.layerDecides')}
                      </Badge>
                    ) : skipped && layerResults[layer] != null ? (
                      <span className="text-xs text-text-muted">
                        {t('tenant.permissions.center.why.layerSkipped')}
                      </span>
                    ) : null}
                  </span>
                </div>
                <ul className="divide-y divide-border-secondary border-t border-border-secondary">
                  {rows.length === 0 ? (
                    <li className="px-4 py-2.5 text-sm text-text-muted">
                      {t(`tenant.permissions.center.why.layerEmpty.${layer}`)}
                    </li>
                  ) : (
                    rows.map((entry) => subjectRow(entry, false))
                  )}
                </ul>
              </Card>
            );
          })
        ) : entries.length === 0 ? (
          <p className="text-sm text-text-secondary">
            {t('tenant.permissions.center.why.noSubjects')}
          </p>
        ) : (
          <Card padding="none" className="overflow-hidden">
            <ul className="divide-y divide-border-secondary">
              {entries.map((entry) => subjectRow(entry, true))}
            </ul>
          </Card>
        )}

        {/* Podsumowanie — co ostatecznie obowiązuje i z której zasady to wynika. */}
        <div className="rounded-xl border border-border-secondary bg-bg-secondary px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-medium text-text-primary">
            {t('tenant.permissions.center.why.resultLabel')}:
            <Badge tone={result ? 'success' : 'danger'} size="md" icon={result ? Check : X}>
              {result
                ? t('tenant.permissions.center.why.resultAllowed')
                : t('tenant.permissions.center.why.resultDenied')}
            </Badge>
          </p>
          {decidedBy && (
            <p className="mt-2 text-sm text-text-secondary">
              {t('tenant.permissions.center.why.decidedBy', {
                subject: t(`tenant.permissions.center.why.subjectOne.${decidedBy.kind}`),
                name: decidedBy.name,
              })}
            </p>
          )}
        </div>

        {/* Zasada działania — ogólne objaśnienie, nie wynik; w tym samym wzorcu podpowiedzi co reszta. */}
        <InfoHint>{reason}</InfoHint>
      </div>
    </Modal>
  );
}
